import { FC } from 'react';
import { ProgressStepper, Step, StepButton, StepLabel } from '@one-thd/sui-atomic-components';
import { TrackingStepper } from '@delivery/tracking';
import { getProgressBarData, getActiveSteps } from '../../utils/tracking-utils';
import { resolveFulfillmentType, getStatusFromCode } from '../../utils/distribution';
import { selectFulfillmentGroup, getFulfillmentGroupStatusCode } from '../../utils/order-details';
import { FULFILLMENT_TYPES } from '../../constants';
import { IActiveDistribution, IDeliveryTrackerData } from '../../models/active-orders';
import { IOrderDetails } from '../../models/order-details';

interface IFulfillmentDeliveryTracker {
  orderDetails?: IOrderDetails;
  /**
   * The card the drawer opened from. A mixed order carries a pickup group and
   * a delivery group, and only the card says which of them this tracker draws.
   */
  distribution?: IActiveDistribution;
  isMobile?: boolean;
}

const getTrackingStatus = (
  deliveryTrackerData?: IDeliveryTrackerData,
  statusDescription?: string,
) => {
  const trackerStatus = deliveryTrackerData?.status;
  if (trackerStatus === 'ORDER_RECEIVED' && deliveryTrackerData?.deliveryWindow?.date) {
    // Align visual stepper progression with Delivery Window status shown in the drawer header.
    return 'SCHEDULED';
  }

  return trackerStatus ?? statusDescription?.replace(/ /g, '_')?.toUpperCase() ?? '';
};

export const FulfillmentDeliveryTracker: FC<IFulfillmentDeliveryTracker> = ({
  orderDetails,
  distribution,
  isMobile,
}) => {
  const group = selectFulfillmentGroup(orderDetails, distribution);

  if (!group) {
    return null;
  }

  const fulfillmentType = resolveFulfillmentType(group?.fulfillmentType, distribution);
  const progressBarData = getProgressBarData(fulfillmentType as string);
  /**
   * Each group runs on its own schedule, so the bar is indexed off this
   * group's status rather than the order root's.
   */
  const statusCode = getFulfillmentGroupStatusCode(group, orderDetails);

  if (
    fulfillmentType === FULFILLMENT_TYPES.BUY_ONLINE_PICKUP_IN_STORE
    || fulfillmentType === FULFILLMENT_TYPES.WILL_CALL_FULFILLMENT
  ) {
    return (
      <ProgressStepper
        leadingLabel=""
        activeStep={getActiveSteps(statusCode ?? 0, fulfillmentType as string)}
        orientation={isMobile ? 'vertical' : 'horizontal'}
        data-testid="fulfillment-stepper"
      >
        {progressBarData?.map((step) => (
          <Step key={step.title} aria-label={[step.title, step.subtitle].filter(Boolean).join('. ').concat('.')}>
            <StepButton icon={step.icon} />
            <StepLabel title={step.title} subtitle={step.subtitle} />
          </Step>
        ))}
      </ProgressStepper>
    );
  }

  if (
    fulfillmentType === FULFILLMENT_TYPES.BUY_ONLINE_SHIP_TO_STORE
    || fulfillmentType === FULFILLMENT_TYPES.SHIP_TO_HOME
    || fulfillmentType === FULFILLMENT_TYPES.BUY_ONLINE_DELIVER_FROM_STORE
    || fulfillmentType === FULFILLMENT_TYPES.APPLIANCE
  ) {
    // `orderDetails` does not carry the delivery-tracker payload, so this path
    // falls through to the status-derived branch of `getTrackingStatus`.
    const deliveryTrackerData = undefined as IDeliveryTrackerData | undefined;

    const fulfillmentTypeMap: { [key: string]: string | undefined } = {
      ShipToHome: 'STH',
      STH: 'ShipToHome',
      BuyOnlineShipToStore: 'BOSS',
      BOSS: 'BuyOnlineShipToStore',
      BuyOnlineDeliverFromStore: 'BODFS',
      BODFS: 'BuyOnlineDeliverFromStore',
      DFS: 'DFS',
      Appliance: 'MA',
      MAJOR_APPLIANCE: 'MA',
      MA: 'Appliance',
      BuyOnlinePickupInStore: 'BOPIS',
      BOPIS: 'BuyOnlinePickupInStore',
      '': undefined,
    };

    const getFulfillmentTypeLink = (type: string | undefined) => fulfillmentTypeMap[type ?? ''] || undefined;

    const fulfillmentTypeLink = getFulfillmentTypeLink(fulfillmentType);

    const statusDescription = getStatusFromCode(statusCode);
    const status = getTrackingStatus(deliveryTrackerData, statusDescription);
    const deliveryType = deliveryTrackerData?.deliveryType || fulfillmentTypeLink;
    const timeliness = deliveryTrackerData?.deliveryWindow?.timeliness ?? '';
    return (
      <TrackingStepper
        status={status}
        deliveryType={deliveryType}
        timeliness={timeliness}
        data-testid="fulfillment-delivery-tracking-stepper"
      />
    );
  }

  return null;
};
